import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Gavel, Loader2, Plus, Search, ShieldAlert, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { stopRow, useRowDetail } from "@/components/common/DetailSheet";
import { ApprovalRow, SlabTable } from "@/components/common/Approvals";
import { MinuteRecord } from "@/components/common/MinuteRecord";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const BLANK = { person_name: "", person_email: "", person_role: "board_trustee",
  interest_type: "directorship", related_party: "", description: "", value: 0 };

export default function Conflicts() {
  const { can } = useAuth();
  const [register, setRegister] = useState(null);
  const [clearances, setClearances] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [dialog, setDialog] = useState("");
  const [probe, setProbe] = useState({ subject: "", amount: 0, decision_type: "grant" });
  const [result, setResult] = useState(null);
  const detail = useRowDetail();
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([api.get("/conflicts/register"), api.get("/conflicts/clearances")]);
    setRegister(r.data);
    setClearances(c.data);
  }, []);

  useEffect(() => { load().catch(() => setRegister(false)); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      setDialog("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const check = async () => {
    setBusy("check");
    try {
      const res = await api.get("/conflicts/check", { params: probe });
      setResult(res.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!register) return <Loading label="Loading the conflicts register" />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-029 · GOV-002 / GOV-004 Conflicts of interest"
        title="Declared interests that actually stop a decision"
        description="Trustees and staff declare directorships, family links, supplier interests and shareholdings, and the register asks for a fresh declaration every year. When a grant recipient or a supplier matches a declared interest, the person who declared it is recused automatically and the decision cannot proceed: a grant follows the authority slab for its value, and a supplier interest goes to a majority vote of the sitting trustees, with approvals and declines both counted on the bar."
        actions={can("conflict", "write") && (
          <Dialog open={dialog === "declare"} onOpenChange={(o) => setDialog(o ? "declare" : "")}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="declare-button"
                      className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1 h-3.5 w-3.5" /> Declare an interest
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader><DialogTitle>Record a declaration</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Person">
                  <Input data-testid="declare-name-input" className="bg-muted/40" value={form.person_name}
                         onChange={(e) => setForm({ ...form, person_name: e.target.value })} />
                </F>
                <F label="Their account email">
                  <Input data-testid="declare-email-input" className="bg-muted/40" value={form.person_email}
                         onChange={(e) => setForm({ ...form, person_email: e.target.value })}
                         placeholder="trustee@demo.servecor.tech" />
                </F>
                <F label="Role">
                  <Select value={form.person_role} onValueChange={(v) => setForm({ ...form, person_role: v })}>
                    <SelectTrigger data-testid="declare-role-select" className="bg-muted/40">
                      <SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["board_trustee", "board_chair", "board_secretary", "treasurer", "staff"].map((r) => (
                        <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Interest type">
                  <Select value={form.interest_type} onValueChange={(v) => setForm({ ...form, interest_type: v })}>
                    <SelectTrigger data-testid="declare-type-select" className="bg-muted/40">
                      <SelectValue /></SelectTrigger>
                    <SelectContent>
                      {register.interest_types.map((t) => (
                        <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <div className="col-span-2"><F label="Related party">
                  <Input data-testid="declare-party-input" className="bg-muted/40" value={form.related_party}
                         onChange={(e) => setForm({ ...form, related_party: e.target.value })}
                         placeholder="Saksham Shiksha Samiti" />
                </F></div>
                <F label="Value of the interest">
                  <Input type="number" data-testid="declare-value-input" className="bg-muted/40"
                         value={form.value}
                         onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} />
                </F>
                <div className="col-span-2"><F label="Nature and mitigation">
                  <Textarea data-testid="declare-description-input" rows={3} className="bg-muted/40"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </F></div>
              </div>
              <Button data-testid="declare-save-button" disabled={busy === "declare"}
                      onClick={() => act("declare", () => api.post("/conflicts/register", form),
                                        "Declaration recorded in the register")}
                      className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "declare" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Record the declaration
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-declarations" label="Declarations" value={register.counters.total}
             accent="sky" icon={Gavel} sub={`${register.counters.active} active`} />
        <Kpi testId="kpi-people" label="People declaring" value={register.counters.people}
             accent="violet" icon={Users} />
        <Kpi testId="kpi-due" label="Due for re-declaration" value={register.counters.due}
             accent="amber" icon={AlertTriangle} />
        <Kpi testId="kpi-open-clearances" label="Blocked decisions" value={clearances?.counters.open || 0}
             accent="red" icon={ShieldAlert} />
        <Kpi testId="kpi-cleared" label="Cleared by the board" value={clearances?.counters.cleared || 0}
             accent="emerald" />
        {can("approval", "vote") && (
          <Kpi testId="kpi-awaiting-your-vote" label="Awaiting your vote"
               value={clearances?.counters.awaiting_you || 0} accent="amber" />
        )}
      </div>

      <Tabs defaultValue="register">
        <TabsList>
          <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          <TabsTrigger value="clearances" data-testid="tab-clearances">
            Conflicted decisions ({clearances?.items.length || 0})
          </TabsTrigger>
          <TabsTrigger value="check" data-testid="tab-check">Check a recipient</TabsTrigger>
        </TabsList>

        <TabsContent value="register" className="mt-4">
          <Panel testId="panel-register" kicker="GOV-002" title="Declared interests">
            <Table testId="register-table" head={<>
              <Th>Reference</Th><Th>Person</Th><Th>Interest</Th><Th>Related party</Th><Th>Value</Th>
              <Th>Declared</Th><Th>Status</Th><Th /></>}>
              {register.items.map((d) => (
                <tr key={d.declaration_ref} className="cursor-pointer hover:bg-muted/40"
                    onClick={() => detail.show(d, d.declaration_ref, d.person_name,
                                               "Declared interest")}
                    data-testid={`declaration-${d.declaration_ref}`}>
                  <Td className="font-mono text-[11px]">{d.declaration_ref}</Td>
                  <Td>
                    <div className="font-semibold text-foreground">{d.person_name}</div>
                    <div className="text-[11px] text-muted-foreground">{d.person_email || "—"}</div>
                  </Td>
                  <Td className="capitalize">{String(d.interest_type).replace(/_/g, " ")}</Td>
                  <Td>{d.related_party}</Td>
                  <Td className="tabular-nums">{d.value ? money(d.value, "INR", true) : "—"}</Td>
                  <Td>{shortDate(d.declared_on)}</Td>
                  <Td>
                    <Chip className={d.status === "active"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-border bg-muted text-muted-foreground"}>{d.status}</Chip>
                    {d.due_for_redeclaration && (
                      <Chip className="ml-1 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                        re-declare
                      </Chip>
                    )}
                  </Td>
                  <Td onClick={stopRow}>
                    {can("conflict", "approve") && d.status === "active" && (
                      <Button size="sm" variant="outline" data-testid={`lapse-${d.declaration_ref}`}
                              disabled={busy === d.declaration_ref}
                              onClick={() => act(d.declaration_ref,
                                () => api.post(`/conflicts/register/${d.declaration_ref}/review`,
                                               { status: "lapsed", note: "Interest ended" }),
                                "Declaration marked as lapsed")}
                              className="h-7 rounded-full border-border text-[11px]">
                        Lapse
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
              {register.items.length === 0 && <Empty colSpan={8} label="Nothing declared yet" />}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="clearances" className="mt-4 space-y-3">
          <Panel testId="panel-clearance-slabs" kicker="Authority" title="How a conflicted decision is cleared">
            <div className="space-y-3 p-4 pt-0">
              <SlabTable slabs={clearances?.slabs || []} />
              <p className="text-[11.5px] text-muted-foreground">
                A conflicted supplier or purchase order always goes to a majority of the sitting
                trustees, whatever the value. The trustees who declared the interest are recused and
                cannot vote{clearances?.anonymous ? ", and votes are anonymous in this tenant" : ""}.
              </p>
            </div>
          </Panel>
          {(clearances?.items || []).map((item) => (
            <ApprovalRow key={item.request_ref} item={item} onDone={load}
                         testId={`clearance-${item.request_ref}`}>
              <MinuteRecord item={item} onDone={load} />
            </ApprovalRow>
          ))}
          {(clearances?.items || []).length === 0 && (
            <div className="panel p-8 text-center text-xs text-muted-foreground"
                 data-testid="empty-clearances">
              No decision is currently held up by a declared interest.
            </div>
          )}
        </TabsContent>

        <TabsContent value="check" className="mt-4">
          <Panel testId="panel-check" kicker="Pre-flight"
                 title="Would this recipient trip the register?">
            <div className="space-y-4 p-4 pt-0">
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="sm:col-span-2"><F label="Recipient or supplier name">
                  <Input data-testid="check-subject-input" className="bg-muted/40" value={probe.subject}
                         onChange={(e) => setProbe({ ...probe, subject: e.target.value })}
                         placeholder="Saksham Shiksha Samiti" />
                </F></div>
                <F label="Amount">
                  <Input type="number" data-testid="check-amount-input" className="bg-muted/40"
                         value={probe.amount}
                         onChange={(e) => setProbe({ ...probe, amount: Number(e.target.value) })} />
                </F>
                <F label="Decision type">
                  <Select value={probe.decision_type}
                          onValueChange={(v) => setProbe({ ...probe, decision_type: v })}>
                    <SelectTrigger data-testid="check-type-select" className="bg-muted/40">
                      <SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grant">Grant or payment</SelectItem>
                      <SelectItem value="supplier">Supplier or purchase order</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
              </div>
              <Button size="sm" data-testid="check-run-button" disabled={busy === "check" || !probe.subject}
                      onClick={check}
                      className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "check" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  : <Search className="mr-1 h-3.5 w-3.5" />} Check the register
              </Button>
              {result && (
                <div className="rounded-xl border border-border bg-muted/30 p-4" data-testid="check-result">
                  {result.conflicted ? (
                    <>
                      <div className="text-sm font-semibold text-red-600 dark:text-red-400">
                        Conflict — this decision would be blocked
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Approval route: {result.route}
                      </div>
                      <ul className="mt-2 space-y-1 text-xs text-foreground/80">
                        {result.matches.map((m) => (
                          <li key={m.declaration_ref}>
                            {m.person_name} · {String(m.interest_type).replace(/_/g, " ")} in{" "}
                            {m.related_party} ({m.declaration_ref})
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                      No declared interest matches — the decision can proceed normally
                    </div>
                  )}
                </div>
              )}
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      {detail.sheet}
    </div>
  );
}
